import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Badge,
  Label,
  Button,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import {
  ISOToHumanReadableDate,
  ISOToHumanReadableWithTimeDate,
} from "components/CommonComponents/CommonFunctions";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";

const assessmentData = {
  assessmentName: "Electrical Safety Basics Test",
  duration: 60,
  status: "ACTIVE",
  availableFrom: "2025-02-01T09:00",
  availableTill: "2025-02-10T17:00",
  passingPercentage: 70,
  maxAttempts: 3,
  totalMarks: 100,
  createdOn: "2025-02-01",
};

const questionList = [
  {
    id: 1,
    question: "What is earthing?",
    type: "MCQ",
    marks: 5,
    options: [
      { text: "Connecting to ground", grade: "100%" },
      { text: "Connecting to neutral", grade: "0%" },
      { text: "Insulation method", grade: "-25%" },
    ],
  },
  {
    id: 2,
    question: "Define electrical hazard.",
    type: "DESC",
    marks: 10,
    options: [],
  },
  {
    id: 3,
    question: "Is PPE mandatory?",
    type: "TF",
    marks: 5,
    options: [
      { text: "True", grade: "100%" },
      { text: "False", grade: "-100%" },
    ],
  },
];

function AssessmentDetail() {
  const navigate = useNavigate();
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const rolePath =
    role === "ADMIN"
      ? "admin"
      : role === "CHANNEL_PARTNER"
        ? "channel-partner"
        : "instructor";
  const { id } = useParams();

  const questionColumns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
    },
    {
      Header: "Question",
      accessor: "question",
    },
    {
      Header: "Options",
      accessor: "options",
      Cell: ({ row }) => {
        const { type, options } = row.original;

        if (type === "DESC") {
          return <span className="text-muted">—</span>;
        }

        return (
          <div className="max-width-25">
            {options.map((opt, idx) => (
              <div key={idx} className="d-flex align-items-baseline mb-1">
                <span className="mr-1">{idx + 1}.</span>
                <span>
                  {opt.text}
                  {opt.grade && (
                    <span className="text-muted ml-1">({opt.grade})</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      Header: "Type",
      accessor: "type",
      Cell: ({ value }) =>
        value === "MCQ"
          ? "Single Choice"
          : value === "MULTI"
            ? "Multiple Choice"
            : value === "TF"
              ? "True / False"
              : "Description",
    },
    {
      Header: "Marks",
      accessor: "marks",
    },
  ];

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i
                    className="fa fa-chevron-left cursor mr-2"
                    onClick={() =>
                      navigate(
                        `/${rolePath}/course/${id}/details?tab=assessment`,
                      )
                    }
                  />
                  <h3 className="mb-0">Assessment Details</h3>
                </div>

                <Button
                  className="btn-deep-blue"
                  onClick={() =>
                    navigate(
                      `/${rolePath}/course/${id}/details?tab=assessment`,
                      {
                        state: { isEdit: true },
                      },
                    )
                  }
                >
                  <i className="fa-solid fa-pencil mr-1" />
                  Edit
                </Button>
              </CardHeader>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <h4 className="mb-0">Assessment Information</h4>
              </CardHeader>

              <CardBody>
                {/* Row 1 */}
                <Row>
                  <Col md="4">
                    <div className="mb-3 d-flex flex-column align-items-start">
                      <Label className="small-label text-muted">
                        Assessment Name
                      </Label>
                      <span className="small-text">
                        {assessmentData.assessmentName}
                      </span>
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="mb-3 d-flex flex-column align-items-start">
                      <Label className="small-label text-muted">
                        Available From
                      </Label>
                      <span className="small-text">
                        {ISOToHumanReadableWithTimeDate(
                          assessmentData.availableFrom,
                        )}
                      </span>
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="mb-3 d-flex flex-column align-items-start">
                      <Label className="small-label text-muted">
                        Available Till
                      </Label>
                      <span className="small-text">
                        {ISOToHumanReadableWithTimeDate(
                          assessmentData.availableTill,
                        )}
                      </span>
                    </div>
                  </Col>
                </Row>

                {/* Row 2 */}
                <Row className="mt-3">
                  <Col md="4">
                    <div className="mb-3 d-flex flex-column align-items-start">
                      <Label className="small-label text-muted">Duration</Label>
                      <span className="small-text">
                        {assessmentData.duration} Minutes
                      </span>
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="mb-3 d-flex flex-column align-items-start">
                      <Label className="small-label text-muted">
                        Total Marks
                      </Label>
                      <span className="small-text">
                        {assessmentData.totalMarks}
                      </span>
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="mb-3 d-flex flex-column align-items-start">
                      <Label className="small-label text-muted">
                        Passing Percentage
                      </Label>
                      <span className="small-text">
                        {assessmentData.passingPercentage}%
                      </span>
                    </div>
                  </Col>
                </Row>

                {/* Row 3 */}
                <Row className="mt-3">
                  <Col md="4">
                    <div className="mb-3 d-flex flex-column align-items-start">
                      <Label className="small-label text-muted">
                        Max Attempts
                      </Label>
                      <span className="small-text">
                        {assessmentData.maxAttempts}
                      </span>
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="mb-3 d-flex flex-column align-items-start">
                      <Label className="small-label text-muted">
                        Created On
                      </Label>
                      <span className="small-text">
                        {ISOToHumanReadableDate(assessmentData.createdOn)}
                      </span>
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="mb-3 d-flex flex-column align-items-start">
                      <Label className="small-label text-muted">Status</Label>
                      <Badge
                        color={
                          assessmentData.status === "ACTIVE"
                            ? "success"
                            : assessmentData.status === "UPCOMING"
                              ? "warning"
                              : "secondary"
                        }
                      >
                        {assessmentData.status}
                      </Badge>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <h4 className="mb-0">Questions</h4>
              </CardHeader>

              <CardBody>
                <CommonTable
                  columns={questionColumns}
                  data={questionList.map((q, index) => ({
                    ...q,
                    srNo: index + 1,
                  }))}
                  defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                  showPaginationBottom={false}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AssessmentDetail;
